export const emptyDay = (date = "") => ({
  date,
  times: [],
  alsoEventIds: {},
});

export const uniqueEventIds = (ids = []) => [
  ...new Set((ids || []).filter(Boolean)),
];

export const alsoEventIdsForTime = (day, time) =>
  uniqueEventIds(day?.alsoEventIds?.[time] || []);

export const eventIdsForCreatedTime = (eventId, day, time) =>
  uniqueEventIds([eventId, ...alsoEventIdsForTime(day, time)]);

export const toggleAlsoEventOnDay = (days, dayIndex, time, alsoEventId) => {
  if (!time || !alsoEventId) return days;
  return days.map((day, index) => {
    if (index !== dayIndex) return day;
    const current = alsoEventIdsForTime(day, time);
    const nextIds = current.includes(alsoEventId)
      ? current.filter((id) => id !== alsoEventId)
      : [...current, alsoEventId];
    const alsoEventIds = { ...(day.alsoEventIds || {}) };
    if (nextIds.length) alsoEventIds[time] = nextIds;
    else delete alsoEventIds[time];
    return { ...day, alsoEventIds };
  });
};

export const nextCalendarDate = (date) => {
  if (!date) return "";
  const [year, month, day] = date.split("-").map(Number);
  const next = new Date(year, month - 1, day + 1);
  const nextYear = next.getFullYear();
  const nextMonth = String(next.getMonth() + 1).padStart(2, "0");
  const nextDay = String(next.getDate()).padStart(2, "0");
  return `${nextYear}-${nextMonth}-${nextDay}`;
};

export const formatDayLabel = (date) => {
  if (!date) return "Pick a date";
  const [year, month, day] = date.split("-").map(Number);
  return new Date(year, month - 1, day, 12, 0, 0).toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
};

export const eventsForHouse = (events, startDate) =>
  (events || []).map((eventItem, index) => ({
    eventId: eventItem.id,
    eventName: eventItem.eventName,
    days: [emptyDay(index === 0 ? startDate : "")],
  }));

export const mergeEventsIntoBlocks = (
  blocks,
  events,
  startDate,
  defaultLocationId = ""
) => {
  if (!events.length) {
    if (!blocks.length) {
      return [{ locationId: defaultLocationId, events: [] }];
    }
    return blocks.map((block) => ({ ...block, events: [] }));
  }
  if (!blocks.length) {
    return [
      {
        locationId: defaultLocationId,
        events: eventsForHouse(events, startDate),
      },
    ];
  }
  return blocks.map((block) => {
    const byId = new Map(block.events.map((item) => [item.eventId, item]));
    return {
      ...block,
      events: events.map((eventItem, index) => {
        const existing = byId.get(eventItem.id);
        if (existing) {
          return { ...existing, eventName: eventItem.eventName };
        }
        return {
          eventId: eventItem.id,
          eventName: eventItem.eventName,
          days: [
            emptyDay(
              block.events.length === 0 && index === 0 ? startDate : ""
            ),
          ],
        };
      }),
    };
  });
};

export const unusedHouseId = (houses, usedIds) =>
  (houses || []).find(
    (house) => !usedIds.includes(house.bowlingCenters.id)
  )?.bowlingCenters?.id || "";

export const houseName = (houses, locationId) =>
  (houses || []).find((house) => house.bowlingCenters.id === locationId)
    ?.bowlingCenters?.centerName || "";

export const updateEventDays = (block, eventId, updater) => ({
  ...block,
  events: block.events.map((eventItem) =>
    eventItem.eventId === eventId
      ? { ...eventItem, days: updater(eventItem.days) }
      : eventItem
  ),
});

export const patchDay = (days, dayIndex, patch) =>
  days.map((day, index) => (index === dayIndex ? { ...day, ...patch } : day));

export const appendDay = (days) => {
  const lastDate = days[days.length - 1]?.date || "";
  return [...days, emptyDay(nextCalendarDate(lastDate))];
};

export const suggestedTimesFromEvents = (events, excludeTimes = []) => {
  const used = new Set(excludeTimes);
  const suggested = [];
  (events || []).forEach((eventItem) => {
    (eventItem.days || []).forEach((day) => {
      (day.times || []).forEach((time) => {
        if (!used.has(time) && !suggested.includes(time)) {
          suggested.push(time);
        }
      });
    });
  });
  return suggested;
};

export const dropDay = (days, dayIndex) =>
  days.length === 1 ? days : days.filter((_, index) => index !== dayIndex);

export const addTimeToDay = (days, dayIndex, time) => {
  if (!time) return days;
  return days.map((day, index) => {
    if (index !== dayIndex || day.times.includes(time)) return day;
    return { ...day, times: [...day.times, time] };
  });
};

export const removeTimeFromDay = (days, dayIndex, time) =>
  days.map((day, index) => {
    if (index !== dayIndex) return day;
    const alsoEventIds = { ...(day.alsoEventIds || {}) };
    delete alsoEventIds[time];
    return {
      ...day,
      times: day.times.filter((item) => item !== time),
      alsoEventIds,
    };
  });

export const squadsFromHouseBlocks = (houseBlocks) => {
  const merged = new Map();
  (houseBlocks || []).forEach((block) => {
    if (!block?.locationId) return;
    (block.events || []).forEach((eventItem) => {
      (eventItem.days || []).forEach((day) => {
        if (!day?.date || !day.times?.length) return;
        day.times.forEach((time) => {
          const key = `${block.locationId}|${day.date}|${time}`;
          const eventIds = eventIdsForCreatedTime(
            eventItem.eventId,
            day,
            time
          );
          const existing = merged.get(key);
          if (existing) {
            existing.eventIds = uniqueEventIds([
              ...existing.eventIds,
              ...eventIds,
            ]);
            existing.eventId = existing.eventIds[0];
            return;
          }
          merged.set(key, {
            eventId: eventIds[0],
            eventIds,
            locationId: block.locationId,
            date: day.date,
            time,
          });
        });
      });
    });
  });
  return [...merged.values()];
};
